import { builderAddPhase, builderMovePhase, builderRemovePhase, isStartOrEnd, type BuilderPlan, type BuilderTree } from '@nsb/core';
import { onBoard } from '../tree/master.ts';
import { useState } from 'react';
import { Confirm, NumberInput } from '../ui.tsx';
import { weeks } from './weeks.ts';

/**
 * The builder's phases (Jason, 2026-10-04: "we could even have phases be
 * adjustable, but the 5 we know by default"): added, reordered and taken out
 * here, with the price's spread at the very start. Each phase's own facts (its
 * name, what happens in it, the gate that closes it, how sure the price is once
 * it closes, its share of the time) are set on its card, opened from its
 * heading on the tree (PhaseCard; Jason, 2026-10-07); the funnel above draws
 * them.
 */

const q = (t: string) => `“${t}”`;
const small = 'link text-[0.8125rem] text-ink-2 hover:text-ink disabled:opacity-40';

export function PhasesEditor({ tree, plan, edit }: { tree: BuilderTree; plan: BuilderPlan; edit: (next: BuilderTree, words: string) => void }) {
  const [name, setName] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);
  const start = Math.round(tree.startSpread * 100);

  return (
    <section className="mt-12">
      <div className="mb-3 flex items-baseline justify-between gap-4 border-b border-rule pb-2">
        <h2 className="text-[1.375rem] sm:text-[1.5rem]">Phases</h2>
      </div>
      <p className="max-w-measure text-ink-2">
        The five the portal knows, to start with, in order. Each runs from the end of the one before until the gate that closes it is reached (or its last step ends, while nothing does), and the price is surer once it closes. A phase’s name, what happens in it, its gate, how sure the price is and its share of the time are set on its card: click its heading on the tree.
      </p>
      <label className="mt-4 flex flex-wrap items-center gap-3">
        <span>The price at the very start, either way: ±</span>
        <NumberInput
          className="!w-20"
          value={start}
          aria-label="The price spread at the start, per cent either way"
          onChange={(n) => {
            const v = Math.max(0, Math.min(200, Math.round(n ?? 0)));
            edit({ ...tree, startSpread: v / 100 }, `The price starts ±${v}% now.`);
          }}
        />
        <span>%</span>
      </label>
      <ol className="mt-4 border-t border-rule">
        {tree.phases.map((p, i) => {
          const planned = plan.phases.find((x) => x.key === p.key);
          // Counted as the board counts a phase's steps: those on it, at the top of the tree, not the start or the end.
          const inIt = onBoard(tree).filter((n) => n.phase === p.key && !isStartOrEnd(n)).length;
          return (
            <li key={p.key} className="grid gap-x-4 gap-y-2 border-b border-rule py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline">
              <div>
                <span className="label">
                  <span className="num">{String(i + 1).padStart(2, '0')}</span>
                </span>{' '}
                <span className="font-display text-[1.25rem]">{p.name}</span>
                <p className="meta mt-1">
                  {planned && planned.end > planned.start ? `${weeks(planned.end - planned.start)} as the board stands` : 'Nothing of it on the board yet'} · <span className="num">{inIt}</span> {inIt === 1 ? 'step' : 'steps'}
                </p>
              </div>
              <span className="flex gap-3">
                <button type="button" className={small} disabled={i === 0} onClick={() => edit(builderMovePhase(tree, p.key, -1), `${q(p.name)} comes earlier now.`)}>
                  Earlier
                </button>
                <button type="button" className={small} disabled={i === tree.phases.length - 1} onClick={() => edit(builderMovePhase(tree, p.key, 1), `${q(p.name)} comes later now.`)}>
                  Later
                </button>
                <button type="button" className={small} disabled={tree.phases.length <= 1} onClick={() => setRemoving(p.key)}>
                  Take out
                </button>
              </span>
              {removing === p.key ? (
                <div className="sm:col-span-2">
                  <Confirm
                    prompt={`Take out ${q(p.name)}?${inIt ? ` Its ${inIt} ${inIt === 1 ? 'step is' : 'steps are'} in no phase until put in one.` : ''}`}
                    onYes={() => {
                      setRemoving(null);
                      edit(builderRemovePhase(tree, p.key), `Took out the phase ${q(p.name)}.`);
                    }}
                    onNo={() => setRemoving(null)}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      <form
        className="mt-3 flex max-w-md gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const t = name.trim();
          if (!t) return;
          edit(builderAddPhase(tree, t).tree, `A new phase, ${q(t)}, at the end.`);
          setName('');
        }}
      >
        <input className="field min-w-0 flex-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="A new phase’s name" aria-label="A new phase’s name" maxLength={80} />
        <button type="submit" className={small} disabled={!name.trim()}>
          Add a phase
        </button>
      </form>
    </section>
  );
}
